// React
import React, { useEffect, useContext } from 'react';

// Navigation
import { useSearchParams } from 'react-router-dom';

// External imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChargingStation,
  faClock,
  faDoorOpen,
  faRoad,
  faTablePicnic,
  faToilet,
  faTruckContainer,
  faWifi,
} from '@fortawesome/pro-regular-svg-icons';
import { useMediaQuery } from "@uidotdev/usehooks";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

// Internal imports
import { isRestStopClosed } from '../../data/restStops';
import OpenSeason from '../OpenSeason';
import RestStopTypeIcon from '../RestStopTypeIcon';
import ShareURLButton from '../../shared/ShareURLButton';
import { MapContext } from '../../../App';

// Styling
import './RestStopPanel.scss';

// Automatic title case conversion for Rest Stop Names and Distance from Municipality
const toTitleCase = (str) => {
  if (!str) return '';

  // 1. Normalize to lowercase, then split by separators (spaces, hyphens, slashes, parens, @).
  return str.toLowerCase().split(/(\s+|-|\/|\(|\)|@)/).map((word, index) => {

    // 2. Return separators as is
    if (/^(\s+|-|\/|\(|\)|@)$/.test(word)) return word;

    // 3. Handle words that should always be UPPERCASE
    if (['bc', 'sb', 'nb', 'eb', 'wb'].includes(word)) return word.toUpperCase();

    // 4. Handle words that should always be lowercase
    if (word === 'km' || word === 'kms') return word;
    if (word === 'of' && index > 0) return word;

    // 5. Handle "Mc" surnames (e.g., McBride, McRae)
    if (word.startsWith('mc') && word.length > 2) {
      return 'Mc' + word.charAt(2).toUpperCase() + word.slice(3);
    }

    // 6. Default: Capitalize the first letter
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join('');
};

// Helper components
const tooltipLargeVehicles = (
  <Tooltip id="tooltipLargeVehicles" className="tooltip-content">
    <p>A commercial vehicle is defined as one that is larger than 20 metres &#40;66 feet&#41; in length.</p>
  </Tooltip>
);

// Main component
export default function RestStopPanel(props) {
  // Misc
  const smallScreen = useMediaQuery('only screen and (max-width: 575px)');

  const { feature, showRouteObjs } = props;

  const restStopData = feature.getProperties();
  const isClosed = isRestStopClosed(restStopData.properties);

  const [searchParams, setSearchParams] = useSearchParams();

  // Context
  const { mapContext } = useContext(MapContext);

  // useEffect hooks
  useEffect(() => {
    const isLargeRestStop = mapContext.visible_layers.largeRestStops;
    const featureType = isLargeRestStop && feature.get("properties").ACCOM_COMMERCIAL_TRUCKS == 'Yes' ? 'largeRestStop' : 'restStop';

    searchParams.set('type', featureType);
    searchParams.set('id', restStopData.id);
    searchParams.delete("display_category");
    setSearchParams(searchParams, { replace: true });
  }, [feature]);

  return (
    <div className={`popup popup--reststop ${isClosed ? 'closed' : ''}`}>
      <div className={`popup__title ${showRouteObjs && !smallScreen ? 'from-route-objs' : ''}`}>
        <div className="popup__title__name">
          <RestStopTypeIcon reststop={restStopData} />
            {isClosed ? (
              <div className='name-div'>
                <p className='name'>Rest area</p>
                <p className="label red-text">Closed</p>
              </div>
              ) : (<p className='name'>Rest area</p>
            )}
        </div>
        <ShareURLButton/>
      </div>
      <div className="popup__content">
        <div className="popup__content__title">
        <p className="name">{toTitleCase(restStopData.properties.REST_AREA_NAME)}</p>
          <p className="location">{toTitleCase(restStopData.properties.DISTANCE_FROM_MUNICIPALITY)}</p>
        </div>
        <div className='popup__content__description'>
          <p className="description-label label">Access</p>
          <div className="data-card">
            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faClock}/>
              </div>
                {restStopData.properties.OPEN_YEAR_ROUND === "Yes" && (
                  <p className="label">Open year round</p>
                )}
                {restStopData.properties.OPEN_YEAR_ROUND === "No" && restStopData.properties.OPEN_DATE && restStopData.properties.CLOSE_DATE && (
                  <div>
                      {<OpenSeason returnState={true} openDate={restStopData.properties.OPEN_DATE} closeDate={restStopData.properties.CLOSE_DATE} /> === "open" ? (
                        <p className="label">Open seasonally</p>
                        ) : ( isRestStopClosed(restStopData.properties) ? (<p className="label">Closed &#40;open seasonally&#41;</p>)
                        : (<p className="label">Open seasonally</p>)
                      )}
                  </div>
                )}
                {restStopData.properties.OPEN_YEAR_ROUND === "No" && !restStopData.properties.OPEN_DATE && !restStopData.properties.CLOSE_DATE && (
                  <p className="label red-text">Closed</p>
                )}
              <p className="data">
                {restStopData.properties.OPEN_YEAR_ROUND === "No" && restStopData.properties.OPEN_DATE && restStopData.properties.CLOSE_DATE && (
                  <OpenSeason openDate={restStopData.properties.OPEN_DATE} closeDate={restStopData.properties.CLOSE_DATE} />
                )}
              </p>
            </div>

            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faDoorOpen} />
              </div>
              <p className="label">
                Entrance
              </p>
              <div className="datas">
                <p className="data">
                  {restStopData.properties.DIRECTION_OF_TRAFFIC} entrance
                </p>
                {restStopData.properties.ACCESS_RESTRICTION === "No Restriction" ? (
                  <p className="data-label">Accessible from both directions</p>
                ) : (
                  <p className="data-label">No <span className="lowercase">{restStopData.properties.ACCESS_RESTRICTION}</span> access</p>
                )}
              </div>
            </div>

            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faTruckContainer} />
              </div>
              <p className="label">
                Large vehicles
              </p>
              {restStopData.properties.ACCOM_COMMERCIAL_TRUCKS === "Yes" ? (
                  <p className="data">Accomodated</p>
                ) : (
                  <p className="data red-text">Not accomodated</p>
              )}
            </div>
          </div>
        </div>

        <div className='popup__content__description'>
          <p className="description-label label">Features</p>

          <div className="data-card">
            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faToilet} />
              </div>
              <p className="label">
                Toilets
              </p>
              <div className="datas">
                <p className="data">
                  {restStopData.properties.NUMBER_OF_TOILETS} {restStopData.properties.TOILET_TYPE} toilet{restStopData.properties.NUMBER_OF_TOILETS > 1 ? 's' : ''}
                </p>
                <p className="label data-label">
                  {restStopData.properties.WHEELCHAIR_ACCESS_TOILET === "Yes" ? (
                  'Wheelchair accessible'
                  ) : (
                    'Not wheelchair accessible'
                  )}
                </p>
              </div>
            </div>
            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faTablePicnic} />
              </div>
              <p className="label">
                Tables
              </p>
                <p className="data">
                  {restStopData.properties.NUMBER_OF_TABLES !== 0 && restStopData.properties.NUMBER_OF_TABLES !== null ? (
                    `${restStopData.properties.NUMBER_OF_TABLES} tables`
                  ) : (
                    `No tables`
                  )}
                </p>
            </div>
            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faWifi} />
              </div>
              <p className="label">
                Wi-Fi
              </p>
              <p className="data">
              {restStopData.properties.WI_FI === "Y" || restStopData.properties.WI_FI === "Yes" ? (
                `Available`
              ) : (
                `Unavailable`
              )}
              </p>
            </div>
            <div className="data-card__row">
              <div className="data-icon">
                <FontAwesomeIcon icon={faRoad} />
              </div>
              <p className="label">
                Lanes
              </p>
              {(restStopData.properties.ACCELERATION_LANE === "No" &&
                restStopData.properties.DECELERATION_LANE === "No") && (
                <div className="datas">
                  <p className="data">No acceleration</p>
                  <p className="data">and deceleration</p>
                </div>
              )}
              {(restStopData.properties.ACCELERATION_LANE === "Yes" &&
                restStopData.properties.DECELERATION_LANE === "Yes") && (
                <div className="datas">
                  <p className="data">Has acceleration</p>
                  <p className="data">and deceleration</p>
                </div>
              )}
              {(restStopData.properties.ACCELERATION_LANE === "Yes" &&
                restStopData.properties.DECELERATION_LANE === "No") && (
                <div className="datas">
                  <p className="data">Has acceleration</p>
                  <p className="data">No deceleration</p>
                </div>
              )}
              {(restStopData.properties.ACCELERATION_LANE === "No" &&
                restStopData.properties.DECELERATION_LANE === "Yes") && (
                <div className="datas">
                  <p className="data">No acceleration</p>
                  <p className="data">Has deceleration</p>
                </div>
              )}
            </div>
            <div className="data-card__row">
              <div className="data-icon">
              </div>
              <p className="label">
                Ev Charging
              </p>
              <div className="datas">
                <div className="data">
                  {restStopData.properties.EV_STATIONS === "Y" ? (
                    'Yes'
                  ) : (
                    'No'
                  )}
                </div>
                <div className="data">
                <a className="plugshare-link footer-link" href="https://www.plugshare.com/" rel="noreferrer" alt="Disclaimer" >
                  {restStopData.properties.EV_STATIONS === "Y" ? (
                    'View on Plugshare'
                  ) : (
                    'Find chargers on Plugshare'
                  )}
                </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
